const AUTO_CURSOR_MODEL_IDS = new Set(['auto', 'default', 'auto-smart']);

function versionParts(id: string): number[] {
  return (id.match(/\d+/g) || []).map((part) => Number(part));
}

function compareLatestGrok(a: { id: string }, b: { id: string }): number {
  const aVersion = versionParts(a.id);
  const bVersion = versionParts(b.id);
  const len = Math.max(aVersion.length, bVersion.length);
  for (let i = 0; i < len; i += 1) {
    const delta = (bVersion[i] || 0) - (aVersion[i] || 0);
    if (delta) return delta;
  }
  return a.id.length - b.id.length;
}

function levelLooksFast(level: { id: string; label?: string }): boolean {
  return /fast/i.test(`${level.id} ${level.label || ''}`);
}

export function preferredRoleChatCursorModel(
  models: Array<{ id: string }>,
  selectedId: string,
): string {
  const selected = String(selectedId || '').trim();
  if (
    selected
    && !AUTO_CURSOR_MODEL_IDS.has(selected.toLowerCase())
    && models.some((model) => model.id === selected)
  ) {
    return selected;
  }
  const groks = models
    .filter((model) => /grok/i.test(model.id) && !AUTO_CURSOR_MODEL_IDS.has(model.id.toLowerCase()))
    .sort(compareLatestGrok);
  if (groks[0]) return groks[0].id;
  const concrete = models.find((model) => !AUTO_CURSOR_MODEL_IDS.has(model.id.toLowerCase()));
  return concrete?.id || models[0]?.id || '';
}

export function preferredRoleChatCursorEffort(
  levels: Array<{ id: string; label?: string }>,
  selectedEffort: string,
): string {
  const selected = String(selectedEffort || '').trim();
  if (selected && levels.some((level) => level.id === selected)) return selected;
  const text = (level: { id: string; label?: string }) => `${level.id} ${level.label || ''}`.toLowerCase();
  const high = levels.find((level) => level.id === 'high' && !levelLooksFast(level))
    || levels.find((level) => !levelLooksFast(level) && !/\bxhigh\b|\bextra high\b/.test(text(level)) && /(^|[^a-z])high([^a-z]|$)/.test(text(level)));
  return high?.id || levels.find((level) => !levelLooksFast(level))?.id || levels[0]?.id || '';
}

export type RoleChatCursorEvent = Record<string, unknown>;
export type RoleChatActiveRunLike = {
  runId: string;
};

export function roleChatAssistantLabel(
  provider: 'openai-agents' | 'cursor-cloud-agent' | undefined,
  roleLabel: string,
): string {
  if (provider === 'cursor-cloud-agent') return roleLabel || 'Role agent';
  if (provider === 'openai-agents') return 'OpenAI';
  return '';
}

function neutralizeProviderName(label: string): string {
  return label
    .replace(/\bCursor SDK\b/gi, 'AI agent')
    .replace(/\bCursor environment\b/gi, 'AI environment')
    .replace(/\bCursor agent\b/gi, 'AI agent')
    .replace(/\bCursor chat\b/gi, 'AI chat')
    .replace(/\bCursor stream\b/gi, 'AI response')
    .replace(/\bcursor\b/gi, 'AI');
}

export function roleChatCursorEventLabel(event: RoleChatCursorEvent): string {
  const type = String(event.type || '');
  if (type === 'status') {
    const status = String(event.status || '').trim();
    const message = String(event.message || status.replace(/_/g, ' ') || '').trim();
    if (status.toUpperCase() === 'THINKING' && message.toLowerCase() === 'thinking') return '';
    return neutralizeProviderName(message);
  }
  if (type === 'run_started') return 'Agent connected';
  if (type === 'cursor_task') {
    const text = String(event.text || '').trim();
    return text ? `Delegated task: ${neutralizeProviderName(text)}` : 'Delegated task activity';
  }
  if (type === 'tool_start') return `Calling ${String(event.name || 'tool')}`;
  if (type === 'tool_done') {
    const status = String(event.status || '');
    return `${String(event.name || 'tool')} ${status === 'completed' ? 'completed' : status || 'finished'}`;
  }
  return type.replace(/_/g, ' ');
}

function isThinkingEvent(event: RoleChatCursorEvent): boolean {
  return String(event.type || '') === 'status'
    && String(event.status || '').trim().toUpperCase() === 'THINKING';
}

function appendThinkingFragment(current: string, fragment: string): string {
  const text = fragment.trim();
  if (!text) return current;
  if (!current) return text;
  if (/^[.,!?;:)]$/.test(text)) return `${current}${text}`;
  return `${current} ${text}`;
}

export function compactRoleChatCursorEvents(events: RoleChatCursorEvent[] | undefined): string[] {
  const labels: string[] = [];
  let thinkingText = '';
  const flushThinking = () => {
    const label = thinkingText.trim().replace(/\s+/g, ' ');
    thinkingText = '';
    if (!label || label.toLowerCase() === 'thinking') return;
    if (labels[labels.length - 1] !== label) labels.push(label);
  };

  for (const event of events || []) {
    if (isThinkingEvent(event)) {
      const label = roleChatCursorEventLabel(event).trim();
      if (!label) continue;
      thinkingText = appendThinkingFragment(thinkingText, label);
      continue;
    }
    flushThinking();
    const label = roleChatCursorEventLabel(event).trim();
    if (!label) continue;
    if (labels[labels.length - 1] === label) continue;
    labels.push(label);
  }
  flushThinking();
  return labels;
}

export type RoleChatActivityCard = {
  kind: 'tool' | 'status' | 'task';
  title: string;
};

export type RoleChatTranscriptBlock =
  | { kind: 'text'; text: string }
  | { kind: 'thinking'; text: string; seconds: number }
  | { kind: 'tool'; name: string; status: string; provider: string; detail: string; output: string }
  | { kind: 'task'; title: string; status: string }
  | { kind: 'status'; text: string };

function flushThinking(blocks: RoleChatTranscriptBlock[], thinkingText: string, thinkingMs: number): { text: string; ms: number } {
  const text = thinkingText.trim().replace(/\s+/g, ' ');
  const seconds = thinkingMs > 0 ? Math.max(1, Math.round(thinkingMs / 1000)) : 0;
  if ((text && text.toLowerCase() !== 'thinking') || seconds > 0) {
    blocks.push({ kind: 'thinking', text: text.toLowerCase() === 'thinking' ? '' : text, seconds });
  }
  return { text: '', ms: 0 };
}

export function buildRoleChatTranscript(
  events: RoleChatCursorEvent[] | undefined,
  content: string,
): RoleChatTranscriptBlock[] {
  const blocks: RoleChatTranscriptBlock[] = [];
  let thinkingText = '';
  let thinkingMs = 0;
  let sawTextDelta = false;
  const openTools = new Map<string, number>();

  for (const event of events || []) {
    const type = String(event.type || '');
    if (type === 'assistant_delta') {
      ({ text: thinkingText, ms: thinkingMs } = flushThinking(blocks, thinkingText, thinkingMs));
      const text = String(event.text || '');
      if (!text) continue;
      sawTextDelta = true;
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'text') last.text += text;
      else blocks.push({ kind: 'text', text });
      continue;
    }
    if (isThinkingEvent(event)) {
      const label = roleChatCursorEventLabel(event).trim();
      if (label) thinkingText = appendThinkingFragment(thinkingText, label);
      const durationMs = Number(event.durationMs || 0);
      if (durationMs > 0) thinkingMs += durationMs;
      continue;
    }
    ({ text: thinkingText, ms: thinkingMs } = flushThinking(blocks, thinkingText, thinkingMs));
    if (type === 'tool_start' || type === 'tool_done') {
      const name = String(event.name || 'tool');
      const callId = String(event.callId || `${name}:${String(event.detail || '')}`);
      const status = type === 'tool_start' ? 'running' : String(event.status || 'finished');
      const provider = String(event.provider || '');
      const detail = String(event.detail || '');
      const output = String(event.output || '');
      const openIndex = openTools.get(callId);
      if (type === 'tool_done' && openIndex !== undefined && blocks[openIndex]?.kind === 'tool') {
        const current = blocks[openIndex];
        blocks[openIndex] = {
          kind: 'tool',
          name,
          status,
          provider: provider || current.provider,
          detail: detail || current.detail,
          output: output || current.output,
        };
        openTools.delete(callId);
      } else {
        blocks.push({ kind: 'tool', name, status, provider, detail, output });
        if (type === 'tool_start') openTools.set(callId, blocks.length - 1);
      }
      continue;
    }
    if (type === 'cursor_task') {
      const title = String(event.text || '').trim() || 'Delegated task';
      blocks.push({ kind: 'task', title: neutralizeProviderName(title), status: String(event.status || '') });
      continue;
    }
    const label = roleChatCursorEventLabel(event).trim();
    if (!label) continue;
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'status' && last.text === label) continue;
    blocks.push({ kind: 'status', text: label });
  }
  ({ text: thinkingText } = flushThinking(blocks, thinkingText, thinkingMs));
  if (!sawTextDelta && String(content || '').trim()) {
    blocks.push({ kind: 'text', text: String(content) });
  }
  return blocks;
}

export function matchTaskToRoleCode(title: string, roleCodes: string[]): string {
  const haystack = title.toLowerCase();
  const matches = roleCodes
    .map((code) => String(code || '').trim())
    .filter((code) => {
      const lower = code.toLowerCase();
      return Boolean(lower) && (haystack.includes(lower) || haystack.includes(lower.replace(/-/g, ' ')));
    });
  return matches.sort((a, b) => b.length - a.length)[0] || '';
}

export function compactRoleChatActivityCards(events: RoleChatCursorEvent[] | undefined): RoleChatActivityCard[] {
  return compactRoleChatCursorEvents(events).map((title) => {
    const lower = title.toLowerCase();
    if (lower.startsWith('delegated task')) return { kind: 'task', title };
    if (lower.startsWith('calling ') || lower.endsWith(' completed') || lower.endsWith(' finished') || lower.endsWith(' error')) {
      return { kind: 'tool', title };
    }
    return { kind: 'status', title };
  });
}

export function formatRoleChatWorkedFor(startIso?: string, endIso?: string): string {
  const start = Date.parse(String(startIso || ''));
  const end = Date.parse(String(endIso || ''));
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return '';
  const totalSec = Math.max(1, Math.round((end - start) / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes <= 0) return `Worked for ${seconds}s`;
  if (seconds === 0) return `Worked for ${minutes}m`;
  return `Worked for ${minutes}m ${seconds}s`;
}

export function removeRoleChatActiveRun<T extends RoleChatActiveRunLike>(
  activeRuns: T[],
  runId: string,
): T[] {
  return activeRuns.filter((run) => run.runId !== runId);
}

export function isLatestRoleChatDetailRequest(currentRequestSeq: number, requestSeq: number): boolean {
  return currentRequestSeq === requestSeq;
}

export function shouldShowRoleChatStreamProgress(state: {
  sending: boolean;
  liveStreaming: boolean;
}): boolean {
  return Boolean(state.sending || state.liveStreaming);
}

export function shouldReplaceRoleChatMessagesOnDetail(state: {
  silent?: boolean;
  liveStreaming: boolean;
}): boolean {
  return !(state.silent && state.liveStreaming);
}

export function resolveRoleChatIdFromMissionNodes(
  nodes: Array<{ roleCode?: string; roleChatId?: string; status?: string }>,
  roleCode: string,
  fallbackChatId?: string,
): string | undefined {
  const matches = (nodes || []).filter((node) => (
    String(node.roleCode || '') === roleCode
    && String(node.roleChatId || '').trim()
  ));
  const active = matches.find((node) => (
    node.status === 'pending'
    || node.status === 'running'
    || node.status === 'delegating'
    || node.status === 'awaiting_children'
  ));
  const chatId = String(active?.roleChatId || matches[matches.length - 1]?.roleChatId || fallbackChatId || '').trim();
  return chatId || undefined;
}

export function isRoleChatPinnedToBottom(
  position: { scrollHeight: number; scrollTop: number; clientHeight: number },
  thresholdPx = 40,
): boolean {
  const { scrollHeight, scrollTop, clientHeight } = position;
  if (scrollHeight <= clientHeight + 1) return true;
  return scrollHeight - scrollTop - clientHeight < thresholdPx;
}
